// src/utils/MintekWordExport.js
//
// MintekWordExport — wraps HTML report content in an Office-Word-compatible
// envelope and saves it as a .doc file. MS Word, Google Docs, and LibreOffice
// all import these cleanly. The API mirrors MintekPdfExport.exportPdf() so the
// SAME htmlContent that drives a PDF export can drive a Word export — just
// swap exportPdf(...) ➜ exportWord(...).
//
// The pdfTable / pdfSection / pdfStatCard / pdfBadge / pdfProgress helpers in
// MintekPdfExport.jsx already emit class-driven HTML; this module re-declares
// the same class names in the Word stylesheet so they render identically.
// (Re-exporting them under word* names below is purely a convenience.)
//
// NOTE on .doc vs .docx: this technique produces an HTML-Word document with
// the .doc extension and application/msword MIME type. It is the industry-
// standard client-side approach (used by Excel exports, JasperReports, SAP,
// Power BI) and is fully editable in Word. A true OOXML .docx would require
// server-side generation (DocumentFormat.OpenXml on the backend) — we can
// add that as a follow-up if Mintek mandates .docx specifically.

import {
  pdfTable as pdfTableHelper,
  pdfSection as pdfSectionHelper,
  pdfStatCard as pdfStatCardHelper,
  pdfBadge as pdfBadgeHelper,
  pdfProgress as pdfProgressHelper
} from './MintekPdfExport';

const MINTEK_RED = '#c8102e';

// Word-flavoured copy of the PDF stylesheet. Word respects most of CSS3 but
// is picky about a few things, so we use point sizes for fonts, avoid `rem`,
// and keep all colours as hex. Class names match MintekPdfExport exactly.
const wordCss = `
  @page WordSection1 { size: A4; margin: 15mm 20mm 20mm 20mm; mso-page-orientation: portrait; }
  div.WordSection1 { page: WordSection1; }
  * { box-sizing: border-box; }
  body { font-family: Calibri, 'Segoe UI', Tahoma, sans-serif; color: #1e293b; font-size: 11pt; line-height: 1.45; }

  .letterhead { border-bottom: 3px solid ${MINTEK_RED}; padding-bottom: 8pt; margin-bottom: 14pt; }
  .letterhead-table { width: 100%; border-collapse: collapse; }
  .letterhead-table td { padding: 0; vertical-align: middle; border: none; }
  .logo-box { width: 36pt; height: 36pt; background: ${MINTEK_RED}; color: white; font-weight: 900; font-size: 18pt; text-align: center; line-height: 36pt; }
  .org-name { font-size: 16pt; font-weight: 800; color: ${MINTEK_RED}; letter-spacing: -0.5pt; padding-left: 8pt; }
  .org-sub { font-size: 7pt; color: #64748b; text-transform: uppercase; letter-spacing: 1.5pt; padding-left: 8pt; }
  .letterhead-right { text-align: right; font-size: 7pt; color: #94a3b8; line-height: 1.6; }

  .report-title { text-align: center; margin: 14pt 0 18pt; }
  .report-title h1 { font-size: 16pt; font-weight: 800; color: #0f172a; margin: 0; }
  .report-title p { font-size: 9pt; color: #64748b; margin-top: 4pt; }

  .section-title { font-size: 11pt; font-weight: 700; color: ${MINTEK_RED}; text-transform: uppercase; letter-spacing: 0.5pt; margin: 14pt 0 6pt; padding-bottom: 3pt; border-bottom: 1px solid #fecaca; }

  table { width: 100%; border-collapse: collapse; margin: 6pt 0; font-size: 9pt; }
  th { background: #f1f5f9; font-weight: 700; text-transform: uppercase; font-size: 7pt; letter-spacing: 0.5pt; color: #64748b; padding: 5pt 6pt; text-align: left; border-bottom: 2px solid #e2e8f0; }
  td { padding: 4pt 6pt; border-bottom: 1px solid #f1f5f9; }

  .stat-card { display: inline-block; padding: 8pt 12pt; border: 1px solid #e2e8f0; border-left: 3px solid ${MINTEK_RED}; margin: 3pt; min-width: 80pt; text-align: center; }
  .stat-card .label { font-size: 7pt; color: #64748b; text-transform: uppercase; font-weight: 600; }
  .stat-card .value { font-size: 16pt; font-weight: 900; color: #0f172a; margin-top: 2pt; }

  .badge { display: inline-block; padding: 2pt 5pt; font-size: 7pt; font-weight: 600; }
  .badge-green { background: #dcfce7; color: #166534; }
  .badge-red { background: #fee2e2; color: #991b1b; }
  .badge-amber { background: #fef3c7; color: #92400e; }
  .badge-blue { background: #dbeafe; color: #1e40af; }

  .progress-bar { display: inline-block; width: 60pt; height: 5pt; background: #f1f5f9; vertical-align: middle; }
  .progress-fill { height: 100%; display: inline-block; }

  .footer { margin-top: 20pt; padding-top: 6pt; border-top: 1px solid #e2e8f0; text-align: center; font-size: 7pt; color: #94a3b8; }

  /* Recommendation cards & callouts used in Portfolio Balancing */
  .reco-card { border-left: 3px solid #c8102e; background: #fff7f8; padding: 7pt 10pt; margin: 4pt 0; }
  .reco-card .reco-title { font-weight: 700; font-size: 9pt; color: #8f1024; }
  .reco-card .reco-body { font-size: 8.5pt; color: #475569; margin-top: 2pt; }
`;

const letterhead = `
  <div class="letterhead">
    <table class="letterhead-table"><tr>
      <td style="width:60%">
        <table><tr>
          <td class="logo-box">M</td>
          <td>
            <div class="org-name">MINTEK</div>
            <div class="org-sub">Council for Mineral Technology</div>
          </td>
        </tr></table>
      </td>
      <td class="letterhead-right">
        200 Malibongwe Drive, Randburg 2194<br/>
        Private Bag X3015, Randburg 2125<br/>
        Tel: +27 11 709 4111 &middot; www.mintek.co.za
      </td>
    </tr></table>
  </div>`;

/**
 * Export an HTML payload as a Microsoft Word document (.doc).
 *
 * @param {string} title    Report title (also used as the page H1)
 * @param {string} subtitle Optional subtitle line under the title (e.g. "Q1 FY 25/26")
 * @param {string} htmlContent  Body HTML — exactly what you pass to exportPdf()
 *
 * Usage:
 *   import { exportWord } from '../utils/MintekWordExport';
 *   import { pdfSection, pdfTable, pdfStatCard } from '../utils/MintekPdfExport';
 *   exportWord('APP Quarterly Report', 'Q1 FY2025/26', `
 *     ${pdfSection('Summary')}
 *     ${pdfStatCard('Total KPIs', 42)}
 *     ${pdfTable(['Ref','Name'], [['STR-001','Strategic Objective 1']])}
 *   `);
 */
export function exportWord(title, subtitle, htmlContent) {
  const dateStr = new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
  const safeName = (title || 'SiloGate_Report').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
  const filename = `${safeName}.doc`;

  const docHtml = `<html xmlns:o='urn:schemas-microsoft-com:office:office'
                          xmlns:w='urn:schemas-microsoft-com:office:word'
                          xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset='utf-8' />
  <title>${title}</title>
  <!--[if gte mso 9]>
  <xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotPromptForConvert/>
      <w:DoNotShowInsertionsAndDeletions/>
    </w:WordDocument>
  </xml>
  <![endif]-->
  <style>${wordCss}</style>
</head>
<body>
  <div class="WordSection1">
    ${letterhead}
    <div class="report-title">
      <h1>${title}</h1>
      ${subtitle ? `<p>${subtitle}</p>` : ''}
      <p>Generated: ${dateStr}</p>
    </div>
    ${htmlContent}
    <div class="footer">
      MINTEK &mdash; Council for Mineral Technology &middot; Generated by SiloGate Platform &middot; ${dateStr} &middot; CONFIDENTIAL
    </div>
  </div>
</body>
</html>`;

  // Prepend a BOM so Word interprets the file as UTF-8.
  const blob = new Blob(['\ufeff', docHtml], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Convenience re-exports — Word uses the same HTML helpers as PDF
// (class names match). If you only import from MintekWordExport in a file,
// you don't need to also import from MintekPdfExport.
export const wordSection  = pdfSectionHelper;
export const wordTable    = pdfTableHelper;
export const wordStatCard = pdfStatCardHelper;
export const wordBadge    = pdfBadgeHelper;
export const wordProgress = pdfProgressHelper;

export default { exportWord, wordSection, wordTable, wordStatCard, wordBadge, wordProgress };
